import {
  Box,
  Container,
  Divider,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Modal,
  Backdrop,
  ButtonGroup,
  Link,
  IconButton
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import uniqid from "uniqid";
import moment from "moment";

import {
  getData,
  utf8ToBase64
} from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { oidButtonSX } from "../user/Account/components/IdentitiesTab";
import { GWWarhammerIcon } from "../../components/icons/gw/GWIcons";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { setLocalStorage } from "../../components/layout/cookies/CookieFunctions";
import { stringToUrl } from "../events/utils/event";
import { handlePagingAll } from "../../components/api/APIUtilities";
import { postNotification } from "../../components/utilities/Notifications";
import { Navigate } from "react-router";
import EventLeaderboardCard from "../../components/layout/EventLeaderboardCard";
import ListResponsive from "../../components/other/ListResponsive";
import { BCPCircleCheck, BCPXmark } from "../../components/icons/bcp/BCPIcons";

const defaultUrl = `${window.location.origin}/login`;
const AUTH_CLIENT_ID = getEnvironmentVariable("AUTH_CLIENT_ID");
const AUTH_HOSTED_UI = getEnvironmentVariable("AUTH_HOSTED_UI");
const wcw25 = {
  eventIds: [
    "KhIQLAymD8Ay", // 40k
    "RuF7lUi7LDvk", // AoS
    "eFHc9v9gauiv", // KT
    "IaDn1zzGAq5b" // UW
  ],
  label: "2025 World Championships of Warhammer",
  hideLabel: true,
  imageUrl: "/images/affiliates/world-champs-25.jpg"
};

function OICLoginButton({ setProcessing, identity, fullWidth }) {
  const [state, setState] = useState(null);

  const loginToOIC = async () => {
    let url = `${AUTH_HOSTED_UI}oauth2/idlink?client_id=${AUTH_CLIENT_ID}&redirect_uri=${stringToUrl(
      defaultUrl
    )}&state=${state}&identity_provider=GamesWorkshop`;
    if (identity?.username) {
      url += `&username=${identity.username}`;
    }
    window.location.assign(url);
  };

  const buttonSX = identity?.isLoggedInWithMyWarhammer
    ? {}
    : oidButtonSX("GamesWorkshop");

  useEffect(() => {
    const stateObj = {
      redirect_uri: defaultUrl,
      client_id: AUTH_CLIENT_ID,
      salt: uniqid()
    };
    const _state = utf8ToBase64(JSON.stringify(stateObj));
    setState(_state);
    setLocalStorage("bcpRedirectUrl", window.location.href);
    setLocalStorage("bcpState", _state);

    // eslint-disable-next-line
  }, []);

  return (
    <>
      {identity?.isLoggedInWithMyWarhammer ? (
        <ButtonGroup fullWidth={fullWidth}>
          <Button
            variant="contained"
            color="success"
            sx={{ textTransform: "unset", ...buttonSX }}
            fullWidth={fullWidth}
            onClick={
              identity?.isLoggedInWithMyWarhammer
                ? null
                : async () => {
                    setProcessing(true);
                    await loginToOIC();
                  }
            }
            startIcon={<GWWarhammerIcon />}
          >
            Logged In with MyWarhammer
          </Button>
          <Button
            variant="contained"
            color="success"
            sx={{ textTransform: "unset", ...buttonSX, maxWidth: "60px" }}
            size="small"
          >
            <BCPCircleCheck />
          </Button>
        </ButtonGroup>
      ) : (
        <Button
          variant="contained"
          color="warning"
          sx={{ textTransform: "unset", ...buttonSX }}
          fullWidth={fullWidth}
          onClick={
            identity?.isLoggedInWithMyWarhammer
              ? null
              : async () => {
                  setProcessing(true);
                  await loginToOIC();
                }
          }
          startIcon={<GWWarhammerIcon />}
        >
          Login with MyWarhammer
        </Button>
      )}
    </>
  );
}

const compStart = "2025-11-13T17:00:00Z";

export default function WarhammerYear2025LandingPage() {
  const authContext = useContext(AuthContext);

  const auth_time = authContext?.authState?.accessTokenDecoded?.auth_time
    ? moment(
        new Date(authContext?.authState?.accessTokenDecoded?.auth_time * 1000)
      )
    : null;
  const successful =
    authContext?.authState?.identity?.isLoggedInWithMyWarhammer &&
    auth_time &&
    auth_time.isAfter(compStart);

  const [processing, setProcessing] = useState(true);
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const loadpage = async () => {
      try {
        if (wcw25.eventIds?.length > 0) {
          const queryStringParameters = {
            limit: 100,
            "eventIds[]": wcw25.eventIds,
            "expand[]": ["gameSystem", "photoFile"]
          };
          var apiEvents = await handlePagingAll(
            "/events",
            queryStringParameters
          );
          let _eventDict = {};
          for (let _event of apiEvents) {
            _eventDict[_event.id] = _event;
          }
          let _events = [];
          for (let eventId of wcw25.eventIds) {
            if (_eventDict[eventId]) _events.push(_eventDict[eventId]);
          }
          setEvents(_events);
        }
      } catch (err) {
        postNotification("error", err);
        Navigate("/");
        return;
      }

      setProcessing(false);
    };
    loadpage();
    // eslint-disable-next-line
  }, []);

  return (
    <Box width="100%">
      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        <Grid container sx={{ p: 2, gap: 2 }}>
          <>
            <Grid item xs={12} textAlign="center">
              <img
                src={wcw25.imageUrl}
                alt={wcw25.label}
                style={{
                  maxHeight: "250px"
                }}
              />
            </Grid>
          </>

          <>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                component="h4"
                sx={{ fontWeight: "bold" }}
                gutterBottom
              >
                Celebrating the biggest year of Warhammer events ever!
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography gutterBottom>
                We're excited to be partnering with Warhammer to celebrate the
                biggest year of Warhammer events ever. Simply by logging in with
                your MyWarhammer account in Best Coast Pairings, you will gain
                free access to the army lists for all 4 World Championships of
                Warhammer events, as well as go in the draw to win a Warhammer
                digital code prize bundle. Full details found at{" "}
                <Link
                  href="https://www.warhammer-community.com/en-gb/articles/r2iyfcit/celebrate-the-2025-season-win-all-the-codexes-and-battletomes/"
                  target="_blank"
                >
                  warhammer-community.com
                </Link>
              </Typography>
            </Grid>

            <Grid item xs={12} textAlign="center">
              <OICLoginButton
                setProcessing={setProcessing}
                identity={authContext?.authState?.identity}
              />
            </Grid>

            <ListResponsive
              originalList={events}
              rowProps={{
                mapFunction: (row, index) => (
                  <Grid key={index} item xs={12} md={6} sx={{ p: 1 }}>
                    <EventLeaderboardCard event={row} limit={4} />
                  </Grid>
                )
              }}
            />

            <Divider sx={{ width: "100%", my: 2 }} />
          </>

          <>
            <Grid item xs={12}>
              <Typography
                variant="h3"
                component="h3"
                sx={{ fontWeight: "bold" }}
                gutterBottom
              >
                What is Best Coast Pairings?
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography>
                Best Coast Pairings (BCP) is the official organized play
                companion for competitive and casual Warhammer events. Whether
                you're a player attending events, an event organizer, or someone
                trying to keep their finger on the pulse of all things
                Warhammer, BCP helps simplify everything from registration, list
                submissions, pairings, scores, and historical data. Our mission
                is to make Warhammer events easier, and facilitate the smoothest
                event experience possible.
              </Typography>
            </Grid>

            <Divider sx={{ width: "100%", my: 2 }} />
          </>

          <>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                component="h4"
                sx={{ fontWeight: "bold" }}
                gutterBottom
              >
                Why Link MyWarhammer?
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography gutterBottom>
                By linking your MyWarhammer account with BCP, you're helping
                streamline the entire event experience:
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <List sx={{ pl: 2 }}>
                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Gain free access to Army Lists for Warhammer sponsored
                    events.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Registration becomes quicker and smoother.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Player identities are verified, so standings and rankings
                    are accurate.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Your event history stays updated and correct.
                  </ListItemText>
                </ListItem>
              </List>
            </Grid>

            <Divider sx={{ width: "100%", my: 2 }} />
          </>

          <>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                component="h4"
                sx={{ fontWeight: "bold" }}
                gutterBottom
              >
                What Services Do We Offer to Players?
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography gutterBottom>
                When you create a FREE Best Coast Pairings account, here's what
                you can do:
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <List sx={{ pl: 2 }}>
                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Find and register for events in your area or globally.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Upload your army list or deck list ahead of time
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>Check-in to the event remotely</ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    View pairings, placings, and results during events for which
                    you are registered.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Receive real-time updates and stay connected with the event
                    beyond just being a competitor.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Event Hosting: Easily create, manage, and host events of any
                    size.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    BCP Organizations allows you to host casual events,
                    campaigns, and game nights — promote your store on our event
                    map and take payments directly through Square.
                  </ListItemText>
                </ListItem>
              </List>
            </Grid>

            <Divider sx={{ width: "100%", my: 2 }} />
          </>

          <>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                component="h4"
                sx={{ fontWeight: "bold" }}
                gutterBottom
              >
                Subscriber Benefits: What You Get When You Upgrade
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography gutterBottom>
                Upgrading to a BCP subscription unlocks advanced features
                designed for serious players and hobby-enthusiasts:
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <List sx={{ pl: 2 }}>
                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    <strong>
                      Unlimited access to all submitted lists globally
                    </strong>{" "}
                    — every round, every pairing, every placing for every game
                    system.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    <strong>
                      Advanced insights & rewards (where available)
                    </strong>{" "}
                    Up-to-date competitive data, community rankings, and
                    priority resources for event participants. Dig deep into
                    data for preparation, analysis, or simply browsing
                    top-tables.
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Follow any event that is happening even if you aren't
                    registered. See pairings, placings, lists, and results for
                    any event anywhere!
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
                >
                  <ListItemText>
                    Subscribers help support the infrastructure and growth of
                    the events ecosystem — you're part of making organized play
                    better.
                  </ListItemText>
                </ListItem>
              </List>
            </Grid>

            <Divider sx={{ width: "100%", my: 2 }} />
          </>

          <Grid item xs={12}>
            <Typography gutterBottom>
              Thanks for being a part of the Best Coast Pairings and Warhammer
              communities.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>See you at the tables!</Typography>
          </Grid>
        </Grid>
      </Container>

      {processing ? (
        <ModalLoadingFull />
      ) : (
        <Modal
          disableAutoFocus
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: ["95vw", "85vw", 600],
              maxHeight: "95vh",

              bgcolor: "background.paper",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: 24,

              p: 4
            }}
          >
            <IconButton
              aria-label="cancel"
              color="error"
              sx={{ position: "absolute", top: "4px", right: "4px" }}
              onClick={() => {
                setOpen(false);
              }}
            >
              <BCPXmark />
            </IconButton>
            <Grid container spacing={1} textAlign="center">
              <Grid item xs={12}>
                {successful ? (
                  <>
                    <Typography
                      variant="h4"
                      component="h4"
                      sx={{ fontWeight: "bold" }}
                      gutterBottom
                    >
                      Success!
                    </Typography>
                    <Typography gutterBottom>
                      You have logged in with your MyWarhammer account,
                      unlocking free access to the army lists for all 4 World
                      Championships of Warhammer events, you have also been
                      entered in the draw to win a Warhammer digital code prize
                      bundle. Full details found at{" "}
                      <Link
                        href="https://www.warhammer-community.com/en-gb/articles/r2iyfcit/celebrate-the-2025-season-win-all-the-codexes-and-battletomes/"
                        target="_blank"
                      >
                        warhammer-community.com
                      </Link>
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h4"
                      component="h4"
                      sx={{ fontWeight: "bold" }}
                      gutterBottom
                    >
                      Celebrating the biggest year of Warhammer events ever!
                    </Typography>
                    <Typography gutterBottom>
                      We're excited to be partnering with Warhammer to celebrate
                      the biggest year of Warhammer events ever. Simply by
                      logging in with your MyWarhammer account in Best Coast
                      Pairings, you will gain free access to the army lists for
                      all 4 World Championships of Warhammer events, as well as
                      go in the draw to win a Warhammer digital code prize
                      bundle. Full details found at{" "}
                      <Link
                        href="https://www.warhammer-community.com/en-gb/articles/r2iyfcit/celebrate-the-2025-season-win-all-the-codexes-and-battletomes/"
                        target="_blank"
                      >
                        warhammer-community.com
                      </Link>
                    </Typography>
                  </>
                )}
              </Grid>
              {successful ? (
                <></>
              ) : (
                <Grid item xs={12}>
                  <OICLoginButton setProcessing={setProcessing} />
                </Grid>
              )}
            </Grid>
          </Box>
        </Modal>
      )}
    </Box>
  );
}
